import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import type { DpPayrollItemKind } from "../api/payrollApi";
import {
  calculateVacationPaymentApi,
  closeVacationPaymentApi,
  DpVacationPayment,
  DpVacationSchedule,
  getVacationPaymentApi,
  getVacationPaymentPdfApi,
  reopenVacationPaymentApi,
  updateVacationPaymentApi,
  vacationPaymentBadge,
} from "../api/vacationsApi";
import { formatCompetence, formatCurrency, formatDateOnly, toDateInputValue } from "../utils/format";
import DocumentFileButton from "./DocumentFileButton";
import ReasonModal from "./ReasonModal";
import FinanceBridgeBox from "./FinanceBridgeBox";

type Props = {
  token: string;
  schedule: DpVacationSchedule;
  onClose: () => void;
  /** Calcular, fechar e reabrir mudam o selo do recibo na lista. */
  onChanged: () => void;
};

const SECTIONS: Array<{ kind: DpPayrollItemKind; title: string }> = [
  { kind: "PROVENTO", title: "Proventos" },
  { kind: "DESCONTO", title: "Descontos" },
  { kind: "INFORMATIVO", title: "Informativos (não entram no líquido)" },
];

const formatReference = (value: string | null) =>
  value === null ? "—" : Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 2 });

const formatDateTime = (value: string | null) =>
  value ? new Date(value).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" }) : "";

/**
 * Recibo de férias de conferência (F2b): prévia ou recibo gravado, com as linhas e a
 * memória de cálculo, a data de pagamento editável enquanto aberto, Calcular, Fechar,
 * Reabrir (com motivo) e o PDF "Aviso e recibo de férias — conferência".
 */
export default function VacationPaymentModal({ token, schedule, onClose, onChanged }: Props) {
  const [payment, setPayment] = useState<DpVacationPayment | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [paymentDate, setPaymentDate] = useState("");
  const [reopening, setReopening] = useState(false);

  useEffect(() => {
    getVacationPaymentApi(token, schedule.id)
      .then((data) => {
        setPayment(data);
        setPaymentDate(toDateInputValue(data.paymentDate));
      })
      .catch((error: any) => toast.error(error?.message || "Erro ao carregar o recibo de férias."))
      .finally(() => setLoading(false));
  }, [token, schedule.id]);

  const run = async (action: () => Promise<DpVacationPayment>, success: string) => {
    setBusy(true);
    try {
      const data = await action();
      setPayment(data);
      setPaymentDate(toDateInputValue(data.paymentDate));
      toast.success(success);
      onChanged();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar o recibo.");
    } finally {
      setBusy(false);
    }
  };

  const closed = payment?.status === "FECHADO";
  const pending = payment?.resultStatus === "PENDENTE";
  const dateChanged = !!payment && paymentDate !== toDateInputValue(payment.paymentDate);
  const badge = payment?.saved
    ? vacationPaymentBadge({ status: payment.status!, resultStatus: payment.resultStatus, netAmount: payment.netAmount, paymentDate: payment.paymentDate })
    : { label: "Prévia", className: "dpBadge--gray" };

  return (
    <>
      <div className="dpModalOverlay">
        <div className="dpModal dpModal--wide">
          <header className="dpModal__header">
            <h2 className="dpModal__title">
              Recibo de férias — {formatDateOnly(schedule.startDate)} a {formatDateOnly(schedule.endDate)}{" "}
              {payment ? <span className={`dpBadge ${badge.className}`}>{badge.label}</span> : null}
            </h2>
            <button className="dpModal__close" onClick={onClose} disabled={busy}>
              &times;
            </button>
          </header>

          <div className="dpModal__body">
            {loading ? (
              <div className="dpEmpty">Carregando...</div>
            ) : !payment ? (
              <div className="dpEmpty">Recibo indisponível.</div>
            ) : (
              <>
                <p className="dpNotice">
                  Recibo de <strong>conferência</strong>: não substitui o aviso e o recibo de férias do contador.{" "}
                  {payment.saved
                    ? "Só o recibo fechado entra na folha do mês do gozo e no 13º."
                    : "Esta é uma prévia calculada agora; nada foi gravado. Clique em Calcular para gravar."}
                </p>

                {payment.warnings.length ? (
                  <div className={`dpNotice ${pending ? "dpNotice--warning" : ""}`}>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {payment.warnings.map((w) => (
                        <li key={w}>{w}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <FinanceBridgeBox
                  view={payment.financeiro}
                  hint="Calcular, mudar a data e Fechar atualizam a previsão no Financeiro; Reabrir ou cancelar as férias cancela o que não tem baixa."
                />

                <div className="dpFormGrid">
                  <div className="dpFormGroup">
                    <label htmlFor="vacPayDate">Data de pagamento</label>
                    <input
                      id="vacPayDate"
                      type="date"
                      className="dpInput"
                      value={paymentDate}
                      disabled={busy || closed}
                      onChange={(e) => setPaymentDate(e.target.value)}
                    />
                    <span className="dpFormHint">
                      Até 2 dias antes do início (art. 145 CLT). Decide a tabela do IR.
                      {payment.suggestedPaymentDate ? ` Sugerida: ${formatDateOnly(payment.suggestedPaymentDate)}.` : ""}
                    </span>
                  </div>
                  <div className="dpFormGroup">
                    {dateChanged && !closed ? (
                      <button
                        type="button"
                        className="dpBtn dpBtn--secondary"
                        style={{ marginTop: 24 }}
                        disabled={busy || !paymentDate}
                        onClick={() =>
                          run(() => updateVacationPaymentApi(token, schedule.id, paymentDate), "Data alterada e recibo recalculado.")
                        }
                      >
                        Salvar data e recalcular
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="dpCounters">
                  {[
                    ["Bruto", formatCurrency(payment.grossAmount)],
                    ["Descontos", formatCurrency(payment.deductionsAmount)],
                    ["Líquido", formatCurrency(payment.netAmount)],
                  ].map(([label, value]) => (
                    <div key={label} className="dpCounter">
                      <div className="dpCounter__value">{value}</div>
                      <div className="dpCounter__label">{label}</div>
                    </div>
                  ))}
                </div>

                {SECTIONS.map(({ kind, title }) => {
                  const lines = payment.lines.filter((l) => l.kind === kind);
                  if (lines.length === 0) return null;
                  return (
                    <section key={kind} className="dpSection">
                      <h3 className="dpSection__title">{title}</h3>
                      <div className="dpTableWrapper">
                        <table className="dpTable">
                          <thead>
                            <tr>
                              <th>Rubrica</th>
                              <th>Referência</th>
                              <th>Valor</th>
                              <th>Como foi calculado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lines.map((l) => (
                              <tr key={l.id}>
                                <td>
                                  <strong>{l.itemName}</strong>
                                  {l.externalCode ? <div className="dpFormHint">Código {l.externalCode}</div> : null}
                                </td>
                                <td>{formatReference(l.reference)}</td>
                                <td>{formatCurrency(l.amount)}</td>
                                <td className="dpFormHint" style={{ whiteSpace: "normal" }}>
                                  {l.memo}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  );
                })}

                {payment.monthParts.length ? (
                  <section className="dpSection">
                    <h3 className="dpSection__title">Parte de cada competência (vai para a folha do mês)</h3>
                    <div className="dpTableWrapper">
                      <table className="dpTable">
                        <thead>
                          <tr>
                            <th>Competência</th>
                            <th>Dias</th>
                            <th>Férias</th>
                            <th>1/3</th>
                            <th>INSS retido</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payment.monthParts.map((p) => (
                            <tr key={`${p.year}-${p.month}`}>
                              <td>{formatCompetence(p.year, p.month)}</td>
                              <td>{p.days}</td>
                              <td>{formatCurrency(p.vacationAmount)}</td>
                              <td>{formatCurrency(p.thirdAmount)}</td>
                              <td>{formatCurrency(p.inssAmount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ) : null}

                {payment.closedAt ? (
                  <p className="dpFormHint">
                    Fechado {payment.closedBy ? `por ${payment.closedBy.nomeCompleto} ` : ""}em {formatDateTime(payment.closedAt)}.
                  </p>
                ) : null}
                {payment.reopenedAt ? (
                  <p className="dpFormHint">
                    Reaberto {payment.reopenedBy ? `por ${payment.reopenedBy.nomeCompleto} ` : ""}em {formatDateTime(payment.reopenedAt)}
                    {payment.reopenReason ? `: ${payment.reopenReason}` : ""}.
                  </p>
                ) : null}
              </>
            )}
          </div>

          <footer className="dpModal__footer">
            {payment?.saved ? (
              <DocumentFileButton
                token={token}
                fetchBlob={() => getVacationPaymentPdfApi(token, schedule.id)}
                fileName={`recibo-ferias-${schedule.startDate.slice(0, 10)}.pdf`}
                label="PDF"
                className="dpBtn dpBtn--secondary"
                disabled={busy || pending}
                title={pending ? "Resolva as pendências e recalcule para imprimir." : closed ? "Abrir o aviso e recibo para imprimir" : "Abrir o aviso e recibo (prévia: recibo aberto)"}
              />
            ) : null}
            {closed ? (
              <button type="button" className="dpBtn dpBtn--secondary" disabled={busy} onClick={() => setReopening(true)}>
                Reabrir recibo
              </button>
            ) : payment ? (
              <>
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary"
                  disabled={busy}
                  onClick={() => run(() => calculateVacationPaymentApi(token, schedule.id), "Recibo calculado.")}
                >
                  Calcular
                </button>
                <button
                  type="button"
                  className="dpBtn dpBtn--primary"
                  disabled={busy || dateChanged}
                  title={dateChanged ? "Salve a data de pagamento antes de fechar." : "Recalcula e congela; vale para a folha do mês e o 13º."}
                  onClick={() => run(() => closeVacationPaymentApi(token, schedule.id), "Recibo fechado.")}
                >
                  Fechar recibo
                </button>
              </>
            ) : null}
          </footer>
        </div>
      </div>

      {reopening ? (
        <ReasonModal
          title="Reabrir recibo de férias"
          description="A folha do mês do gozo volta a ficar pendente até o recibo ser fechado de novo."
          confirmLabel="Reabrir"
          onClose={() => setReopening(false)}
          onConfirm={async (reason) => {
            const data = await reopenVacationPaymentApi(token, schedule.id, reason);
            setPayment(data);
            setReopening(false);
            toast.success("Recibo reaberto.");
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}
